<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    header="Assign Bank Account"
    :style="{ width: '40rem' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    :closable="!isAssigning"
    :closeOnEscape="!isAssigning"
    data-test-id="bank-account-assignment-dialog"
    @update:visible="handleVisibilityChange"
  >
    <div class="space-y-4">
      <!-- Loading State -->
      <div
        v-if="isLoadingBankAccounts"
        class="flex flex-col items-center justify-center py-8"
        data-test-id="loading-bank-accounts"
      >
        <i class="pi pi-spin pi-spinner text-2xl text-surface-400 dark:text-surface-500 mb-2"></i>
        <p class="text-sm text-surface-600 dark:text-surface-300">Loading bank accounts...</p>
      </div>

      <!-- Error State -->
      <div
        v-else-if="loadError"
        class="bg-red-50 border border-red-200 rounded-lg p-4"
        data-test-id="bank-accounts-error"
      >
        <div class="flex items-start gap-2">
          <i class="pi pi-exclamation-triangle text-red-500 mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-red-800">Failed to load bank accounts</p>
            <p class="text-sm text-red-600 mt-1">{{ loadError }}</p>
          </div>
        </div>
      </div>

      <!-- Bank Accounts List -->
      <div v-else class="space-y-3">
        <p class="text-sm text-surface-600 dark:text-surface-300">
          Select a bank account to assign to this payout, or mark as "No Bank Account".
        </p>

        <div class="space-y-2 max-h-[400px] overflow-y-auto">
          <!-- Bank Account Options -->
          <div
            v-for="account in availableBankAccounts"
            :key="account.Id"
            @click="handleSelect(account.Id)"
            class="cursor-pointer"
          >
            <BankAccountListItem
              :bank-account="account"
              :selectable="true"
              :selected-id="selectedBankAccountId"
              @select="handleSelect"
            />
          </div>

          <!-- No Bank Account Option -->
          <div @click="handleSelect(null)" class="cursor-pointer">
            <BankAccountListItem
              :selectable="true"
              :selected-id="selectedBankAccountId"
              :is-no-bank-account="true"
              @select="handleSelect"
            />
          </div>

          <!-- Empty State -->
          <div
            v-if="availableBankAccounts.length === 0"
            class="text-center py-8 text-surface-500 dark:text-surface-400"
            data-test-id="no-bank-accounts-available"
          >
            <i class="pi pi-inbox text-3xl mb-2"></i>
            <p class="text-sm">No bank accounts available</p>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          @click="handleCancel"
          outlined
          :disabled="isAssigning"
          data-test-id="cancel-assignment-btn"
        />
        <Button
          label="Assign"
          icon="pi pi-check"
          @click="handleAssign"
          severity="primary"
          :disabled="isAssigning || isLoadingBankAccounts"
          :loading="isAssigning"
          data-test-id="confirm-assignment-btn"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import BankAccountListItem from './BankAccountListItem.vue'
import { useShopifyPayoutsStore } from '@/stores/shopifyPayoutsStore'
import { successToast, errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'

interface Props {
  visible: boolean
  payoutId: number
  merchantId: string
  storeId: string
  businessEntityId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  assigned: []
}>()

const toast = useToast()
const { authState } = useFrontegg()
const store = useShopifyPayoutsStore()

const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
})

const selectedBankAccountId = ref<string | null>(null)
const loadError = ref<string | null>(null)

const {
  availableBankAccounts,
  isLoadingBankAccounts,
} = storeToRefs(store)

const isAssigning = computed(() => store.isAssigningBankAccount)

watch(
  () => props.visible,
  async newVisible => {
    if (newVisible) {
      selectedBankAccountId.value = null
      loadError.value = null
      try {
        await store.fetchBankAccounts(
          authState,
          props.merchantId,
          props.storeId,
          props.businessEntityId,
        )
      } catch (error: unknown) {
        loadError.value =
          error instanceof Error
            ? error.message
            : 'Failed to load bank accounts'
      }
    } else {
      // Clear selection when dialog closes
      selectedBankAccountId.value = null
      loadError.value = null
      store.clearBankAccountAssignmentState()
    }
  },
  { immediate: true },
)

function handleVisibilityChange(value: boolean) {
  dialogVisible.value = value
}

function handleSelect(value: string | null) {
  if (!isAssigning.value) {
    selectedBankAccountId.value = value
  }
}

function handleCancel() {
  if (!isAssigning.value) {
    dialogVisible.value = false
  }
}

async function handleAssign() {
  if (isAssigning.value) return

  try {
    await store.assignBankAccount(
      authState,
      props.merchantId,
      props.storeId,
      props.businessEntityId,
      props.payoutId,
      selectedBankAccountId.value,
    )

    if (selectedBankAccountId.value === null) {
      successToast(
        toast,
        'Success',
        'Marked as no bank account',
        'assignment-success',
      )
    } else {
      successToast(
        toast,
        'Success',
        'Bank account assigned successfully',
        'assignment-success',
      )
    }

    dialogVisible.value = false
    emit('assigned')
  } catch (error: unknown) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}
</script>
